"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TextArea = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _noop = require("../../constants/noop");
var _useTheme = require("../../theme/utils/useTheme");
var _focusMode = require("../focusMode");
var _formInputTheme = require("../textinput/styled/formInputTheme");
var _TextAreaStyled = require("./styled/TextAreaStyled");
/** @jsxRuntime classic */
/** @jsx jsx */

const DEFAULT_MIN_LINES = 4;
const TextArea = /*#__PURE__*/(0, _react.forwardRef)((props, parentRef) => {
  const {
    // We need an e2e test for this prop.
    // See https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/326
    disableManualResize = false,
    value,
    onChange,
    minHeight,
    maxHeight,
    clientId,
    isDisabled = false,
    style: propStyle,
    validationState,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyUp,
    onMouseEnter,
    onMouseLeave,
    ...htmlAttributes
  } = props;
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  const borderWidth = _formInputTheme.formInputTheme.borderWidth.rest(themeMode);
  const defaultHeight = borderWidth * 2 + _TextAreaStyled.VERTICAL_PADDING * 2 + DEFAULT_MIN_LINES * Math.floor(_TextAreaStyled.LINE_HEIGHT_PX);
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const [isHovered, setIsHovered] = (0, _react.useState)(false);
  const textAreaRef = (0, _lodestarUtils.useEnsuredForwardedRef)(parentRef);

  // The inline value for CSS height. Values transitions might look like:
  // 0 => 60px => 0 => 60px => 0 => 80px ...
  // The momentary "0" value rubber bands the height to the content
  const [height, setHeight] = (0, _react.useState)("0");

  // The height as reported by ResizeObserver (available in all browsers
  // except IE). This value changes for 3 reasons:
  // 1. Initial state is reported as soon as you register an observer
  // 2. Auto-grow/shrink based on more or less content
  // 3. Manually dragging the resize handle
  const [observedHeight, setObservedHeight] = (0, _react.useState)(0);

  // The height that the user manually dragged the vertical resize handle
  // to.
  const [manualHeight, setManualHeight] = (0, _react.useState)(0);

  // The most recently calculated content height, as generated by momentarily
  // setting the height to auto.
  const [contentHeight, setContentHeight] = (0, _react.useState)(0);
  function handleOnChange(e) {
    if (onChange) {
      onChange(e.target.value, e);
    }
  }
  const handleKeyDown = (0, _lodestarUtils.useIsComposingHandler)(onKeyDown || _noop.noop);
  const handleKeyUp = (0, _lodestarUtils.useIsComposingHandler)(onKeyUp || _noop.noop);

  // Place this effect first so the initial run doesn't reset the height
  (0, _react.useEffect)(() => {
    // This momentarily removes the explicit numeric height, so that
    // the native content height can be interrogated.
    setHeight("0");
  }, [value]);
  (0, _react.useEffect)(() => {
    if (textAreaRef?.current !== null) {
      const newHeight = textAreaRef.current.scrollHeight + _TextAreaStyled.SIZING_BUFFER + borderWidth * 2;

      // Use an additional small number for the comparison here to avoid rounding errors from zoom.
      // Without any extra padding, even a tiny floating point or zoom error could result in an infinite loop.
      const CHANGE_SIZE_MINIMUM_DELTA = 2;
      const hasHeightChangedEnough = Math.abs(newHeight - contentHeight) > _TextAreaStyled.SIZING_BUFFER + CHANGE_SIZE_MINIMUM_DELTA;
      if (hasHeightChangedEnough || height === "0") {
        setHeight(`${manualHeight ? manualHeight : newHeight}px`);
      }
      if (hasHeightChangedEnough) {
        setContentHeight(newHeight);
      }
    }
  }, [contentHeight, height, manualHeight, borderWidth, textAreaRef, textAreaRef.current?.scrollHeight]);
  (0, _react.useEffect)(() => {
    if (disableManualResize || !window.ResizeObserver) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (textAreaRef.current) {
        const height = textAreaRef.current.getBoundingClientRect().height;
        setObservedHeight(height);
      }
    });
    if (textAreaRef.current) {
      observer.observe(textAreaRef.current);
    }
    return () => observer.disconnect();
  }, [textAreaRef, height, disableManualResize]);

  // Need this to only run when observedHeight changes, so not
  // including contentHeight despite the lint warning. If we included
  // contentHeight as well, the box erroroneously updates
  // manualHeight when no drag handle operation occurred.
  const latestContentHeightRef = (0, _lodestarUtils.useLiveRef)(contentHeight);
  (0, _react.useEffect)(() => {
    if (disableManualResize) {
      return;
    }
    // ResizeObserver will notify regardless of if the change was programmatic
    // (smart resize) or manual (drag handle). To disambiguate we can use a bit
    // of lookbehind to compare the last calculated content height with the
    // currently reported ResizeObserver height. This avoids ugly hacks like
    // timers.
    if (Math.abs(observedHeight - latestContentHeightRef.current) > _TextAreaStyled.SIZING_BUFFER) {
      setManualHeight(observedHeight);
    }
  }, [observedHeight, disableManualResize, latestContentHeightRef]);

  // Disable drag handle in IE
  const resizeStyleOverride = {
    resize: window.ResizeObserver ? undefined : "none"
  };
  return (0, _core.jsx)(_TextAreaStyled.TextAreaStyled, (0, _extends2.default)({
    isDisabled: isDisabled,
    isFocused: isFocused,
    isHovered: isHovered,
    disableManualResize: disableManualResize,
    value: value,
    focusMode: focusMode,
    onChange: handleOnChange,
    onFocus: (0, _lodestarUtils.callAllHandlers)(() => setIsFocused(true), onFocus),
    onBlur: (0, _lodestarUtils.callAllHandlers)(() => setIsFocused(false), onBlur),
    onKeyDown: onKeyDown && handleKeyDown || undefined,
    onKeyUp: onKeyUp && handleKeyUp || undefined,
    onMouseEnter: (0, _lodestarUtils.callAllHandlers)(() => setIsHovered(true), onMouseEnter),
    onMouseLeave: (0, _lodestarUtils.callAllHandlers)(() => setIsHovered(false), onMouseLeave),
    ref: textAreaRef,
    validationState: validationState,
    style: {
      ...propStyle,
      height,
      maxHeight: maxHeight,
      minHeight: minHeight || defaultHeight,
      ...resizeStyleOverride
    },
    "data-client-id": clientId,
    themeMode: themeMode,
    "aria-disabled": isDisabled
  }, htmlAttributes, {
    readOnly: isDisabled ? true : htmlAttributes.readOnly ? htmlAttributes.readOnly : false
  }));
});
exports.TextArea = TextArea;
//# sourceMappingURL=TextArea.js.map